import { useMemo, useCallback, useState } from 'react';
import { useTracking } from 'react-tracking';

import { isISO2 } from '@/constants/countries/ISO2';

import { BrokerageComboBox } from './BrokerageComboBox';
import { BrokerageSearchDrawer } from './BrokerageSearchDrawer';
import { useBrokerSearch } from '../../hooks/useBrokerSearch';
import type { Brokerage } from '../../hooks/useBrokerSearch';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useIsMobileScreen } from '@/hooks/useMedia';

interface Props {
  brokerage?: Brokerage;
  onSelect: (brokerage: Brokerage) => void;
}

export const Brokerages = ({ brokerage, onSelect }: Props) => {
  const { isMobile } = useUserAgent();
  const isMobileScreen = useIsMobileScreen({ defaultMatches: isMobile });

  const [inputValue, setInputValue] = useState('');

  const { data, status: brokeragesStatus } = useBrokerSearch({
    searchInput: inputValue.trim(),
  });

  const brokerages = data?.searchBrokerages;

  const { Track, trackEvent } = useTracking({
    type: 'track',
    modifier: 'connectWithBrokerage',
  });

  const options = useMemo(() => {
    if (brokeragesStatus !== 'success') return [];
    return (
      brokerages?.map((brokerage) => ({
        url: brokerage.url,
        countryISO: isISO2(brokerage.countryISO) ? brokerage.countryISO : null,
        id: brokerage.id,
        label: brokerage.countryISO
          ? `${brokerage.name} (${brokerage.countryISO})`
          : brokerage.name,
        name: brokerage.name,
      })) || []
    );
  }, [brokerages, brokeragesStatus]);

  const handleSelect = useCallback(
    (brokerageId: Brokerage['id']) => {
      const brokerage = brokerages?.find((n) => n.id === brokerageId);
      if (!brokerage) return;
      onSelect(brokerage);
      trackEvent({ action: 'select', brokerageId });
      setInputValue('');
    },
    [onSelect, brokerages, trackEvent]
  );

  const isBrokeragesPortalOpen = typeof brokerage?.id !== 'undefined';

  return (
    <Track>
      <div className="grid gap-x2">
        {isMobileScreen ? (
          <BrokerageSearchDrawer
            options={options}
            disabled={isBrokeragesPortalOpen}
            onFocus={() => trackEvent({ action: 'focus' })}
            onSelect={handleSelect}
            inputValue={inputValue}
            onInputChange={setInputValue}
            status={brokeragesStatus}
          />
        ) : (
          <BrokerageComboBox
            options={options}
            disabled={isBrokeragesPortalOpen}
            onFocus={() => trackEvent({ action: 'focus' })}
            onSelect={handleSelect}
            inputValue={inputValue}
            onInputChange={setInputValue}
            status={brokeragesStatus}
          />
        )}
      </div>
    </Track>
  );
};
